import type React from 'react';

import { getDisplayName } from 'utils/getDisplayName/getDisplayName';

export type WithFocusOnErrorProps = {
  focusOnFirstError: () => void;
};

const WithFocusOnError = <T extends WithFocusOnErrorProps>(
  WrappedComponent: React.ComponentType<T>,
) => {
  const FOCUSABLE_ELEMENTS = '[aria-invalid="true"]';
  const WAIT_TIME = 150;

  const WithFocusOnError = (props: Omit<T, keyof WithFocusOnErrorProps>) => (
    <WrappedComponent {...(props as T)} focusOnFirstError={focusOnFirstError} />
  );

  WithFocusOnError.displayName = `WithFocusOnError(${getDisplayName(
    WrappedComponent,
  )})`;

  const focusOnFirstError = () => {
    const firstErrorNode = document.querySelector(FOCUSABLE_ELEMENTS);

    if (!firstErrorNode) {
      return;
    }

    try {
      firstErrorNode.scrollIntoView({
        block: 'center',
      });

      const focusableElement = firstErrorNode as HTMLInputElement;

      setTimeout(() => {
        (focusableElement as HTMLInputElement).focus({
          preventScroll: false,
        });

        focusableElement.select();
      }, WAIT_TIME);
    } catch (_e) {
      firstErrorNode.scrollIntoView();
    }
  };

  return WithFocusOnError;
};

export { WithFocusOnError };
